'use client';

/* VAMP explainer + interactive ratio/savings calculator + FAQ accordion. */
import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Icon } from '../icon';
import { SectionHead, toneVar, useReveal } from '../primitives';
import { faqs, href, vampThreshold } from '../data';
import type { Faq, Tone } from '../data';

function fmtMoney(n: number): string {
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
  return '$' + Math.round(n).toLocaleString('en-US');
}

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  fmt: (v: number) => string;
}

function Slider({ label, value, min, max, step, onChange, fmt }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="stack" style={{ gap: 9 }}>
      <div className="between">
        <span className="muted" style={{ fontSize: 13.5 }}>{label}</span>
        <span className="tnum" style={{ fontSize: 15, fontWeight: 600 }}>{fmt(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(+e.target.value)}
        className="vamp-range" style={{ '--p': pct + '%' } as CSSProperties} />
    </div>
  );
}

export function VampSection() {
  useReveal();
  const T = vampThreshold;
  const [txns, setTxns] = useState<number>(60000);
  const [rate, setRate] = useState<number>(0.9);     // current fraud+dispute rate %
  const [avg, setAvg] = useState<number>(120);        // avg transaction value $

  const r = useMemo(() => {
    const disputes = txns * (rate / 100);
    const fee = 25;
    const exposureBefore = disputes * (avg + fee);
    const reduction = 0.86;                     // illustrative combined lifecycle reduction
    const effRatio = rate * (1 - reduction);
    const exposureAfter = exposureBefore * (1 - reduction);
    const protectedAmt = exposureBefore - exposureAfter;
    return { disputes, exposureBefore, exposureAfter, effRatio, protectedAmt };
  }, [txns, rate, avg]);

  const scaleMax = 1.2;
  const beforePct = Math.min(100, (rate / scaleMax) * 100);
  const afterPct = Math.min(100, (r.effRatio / scaleMax) * 100);
  const threshPct = (T.soon / scaleMax) * 100;
  const overThreshold = rate > T.soon;

  return (
    <section className="section" id="vamp" style={{ background: 'var(--bg-sunken)', borderBlock: '1px solid var(--line-soft)' }}>
      <div className="container">
        <div className="vamp-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.92fr) minmax(0, 1.08fr)', gap: 'clamp(28px, 4vw, 60px)', alignItems: 'center' }}>
          {/* left: explainer */}
          <div className="stack reveal" style={{ gap: 22 }}>
            <SectionHead eyebrow="Visa VAMP · 2025–2026"
              title="The thresholds just got tighter. Get ahead of them."
              lead="From April 2025, TC-40 fraud alerts resolved through RDR count toward your VAMP ratio — and the ceiling keeps dropping. The lifecycle attacks the ratio upstream, before alerts are ever filed." />
            <div className="stack" style={{ gap: 12 }}>
              {([
                { icon: 'trending-down', tone: 'rose', h: 'Threshold falling 0.9% → 0.3%', s: 'Acquirers will hold you to even stricter internal buffers to protect their portfolios.' },
                { icon: 'circle-alert', tone: 'amber', h: 'TC-40 via RDR now counts', s: 'Fraud chargebacks hit your ratio even when resolved — though non-fraud TC-15 stays excluded.' },
                { icon: 'clock', tone: 'blue', h: 'One 3-month grace period', s: 'Granted once per rolling 12 months. Catching trends early is the only reliable defense.' },
              ] as { icon: string; tone: Tone; h: string; s: string }[]).map((x, k) => {
                const c = toneVar(x.tone);
                return (
                  <div key={k} className="card" style={{ padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <span style={{ width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', color: c, background: `color-mix(in oklch, ${c} 14%, transparent)`, border: `1px solid color-mix(in oklch, ${c} 28%, transparent)`, flexShrink: 0 }}><Icon name={x.icon} size={19} /></span>
                    <div className="stack" style={{ gap: 2 }}>
                      <span style={{ fontSize: 15, fontWeight: 550 }}>{x.h}</span>
                      <span className="subtle pretty" style={{ fontSize: 13 }}>{x.s}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* right: calculator */}
          <div className="card reveal" style={{ padding: 'clamp(22px, 3vw, 34px)', position: 'relative', overflow: 'hidden', transitionDelay: '90ms' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(500px 240px at 90% -10%, oklch(0.5 0.17 258 / 0.22), transparent 62%)', pointerEvents: 'none' }} />
            <div className="between" style={{ marginBottom: 22, position: 'relative' }}>
              <span className="eyebrow no-rule">VAMP & savings model</span>
              <span className="tag">Illustrative</span>
            </div>
            <div className="stack" style={{ gap: 18, position: 'relative' }}>
              <Slider label="Monthly transactions" value={txns} min={5000} max={500000} step={5000} onChange={setTxns} fmt={v => v.toLocaleString('en-US')} />
              <Slider label="Current fraud + dispute rate" value={rate} min={0.1} max={1.2} step={0.05} onChange={setRate} fmt={v => v.toFixed(2) + '%'} />
              <Slider label="Average transaction value" value={avg} min={20} max={500} step={5} onChange={setAvg} fmt={v => '$' + v} />
            </div>

            {/* VAMP gauge */}
            <div className="stack" style={{ gap: 10, marginTop: 26, position: 'relative' }}>
              <div className="between" style={{ fontSize: 12.5 }}>
                <span className="muted">Your VAMP ratio vs the 0.3% ceiling</span>
                <span className="row" style={{ gap: 6, color: overThreshold ? 'var(--rose)' : 'var(--green)', fontWeight: 500 }}>
                  <Icon name={overThreshold ? 'circle-alert' : 'circle-check'} size={14} />{overThreshold ? 'Over threshold' : 'Within threshold'}
                </span>
              </div>
              <div style={{ position: 'relative', height: 42 }}>
                <div style={{ position: 'absolute', top: 14, left: 0, right: 0, height: 14, borderRadius: 8, background: 'var(--bg-sunken)', border: '1px solid var(--line)', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, width: beforePct + '%', background: 'linear-gradient(90deg, color-mix(in oklch, var(--rose) 60%, transparent), var(--rose))', transition: 'width .4s', opacity: 0.5 }} />
                  <div style={{ position: 'absolute', inset: 0, width: afterPct + '%', background: 'linear-gradient(90deg, var(--cyan), var(--green))', transition: 'width .5s' }} />
                </div>
                {/* threshold marker */}
                <div style={{ position: 'absolute', top: 6, bottom: 6, left: threshPct + '%', width: 2, background: 'var(--fg)' }} />
                <span className="mono" style={{ position: 'absolute', top: -2, left: `calc(${threshPct}% + 6px)`, fontSize: 10, color: 'var(--fg-muted)' }}>0.30%</span>
              </div>
              <div className="between" style={{ fontSize: 12 }}>
                <span className="row" style={{ gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--rose)', opacity: 0.6 }} />Now <b className="tnum" style={{ color: 'var(--fg)' }}>{rate.toFixed(2)}%</b></span>
                <span className="row" style={{ gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--green)' }} />With FRAUDefender <b className="tnum" style={{ color: 'var(--green)' }}>{r.effRatio.toFixed(2)}%</b></span>
              </div>
            </div>

            {/* result cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 22, position: 'relative' }}>
              <div className="card" style={{ padding: '16px 18px', background: 'var(--bg-sunken)' }}>
                <div className="subtle" style={{ fontSize: 12 }}>Monthly exposure now</div>
                <div className="stat-fig tnum" style={{ fontSize: 30, marginTop: 6, color: 'var(--fg-soft)' }}>{fmtMoney(r.exposureBefore)}</div>
                <div className="subtle" style={{ fontSize: 11.5, marginTop: 2 }}>{Math.round(r.disputes).toLocaleString()} disputes/mo</div>
              </div>
              <div className="card" style={{ padding: '16px 18px', background: 'color-mix(in oklch, var(--green) 9%, var(--bg-sunken))', borderColor: 'color-mix(in oklch, var(--green) 30%, transparent)' }}>
                <div className="row" style={{ gap: 6, color: 'var(--green)', fontSize: 12 }}><Icon name="shield-check" size={14} />Protected / recovered</div>
                <div className="stat-fig tnum" style={{ fontSize: 30, marginTop: 6, color: 'var(--green)' }}>{fmtMoney(r.protectedAmt)}</div>
                <div className="subtle" style={{ fontSize: 11.5, marginTop: 2 }}>≈86% of exposure addressed</div>
              </div>
            </div>
            <a href={href('enroll')} className="btn btn-primary" style={{ width: '100%', marginTop: 16, position: 'relative' }}>Model this for your portfolio<Icon name="arrow-right" size={17} /></a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  useReveal();
  const items: Faq[] = faqs;
  const [open, setOpen] = useState<number>(0);
  return (
    <section className="section" id="faq">
      <div className="container">
        <div className="faq-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.7fr) minmax(0, 1.3fr)', gap: 'clamp(28px, 5vw, 72px)', alignItems: 'start' }}>
          <div className="reveal" style={{ position: 'sticky', top: 100 }}>
            <SectionHead eyebrow="Questions" title="The details that matter" lead="How the agentic layer, the gates, and the card-network rules actually fit together." />
          </div>
          <div className="stack reveal" style={{ gap: 0 }}>
            {items.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} style={{ borderTop: '1px solid var(--line-soft)', borderBottom: i === items.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} className="between" style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '22px 0', gap: 16, color: 'var(--fg)' }}>
                    <span style={{ fontSize: 17, fontWeight: 500 }}>{f.q}</span>
                    <span style={{ flexShrink: 0, transition: 'transform .25s', transform: isOpen ? 'rotate(45deg)' : 'none', color: 'var(--primary-2)' }}><Icon name="plus" size={20} /></span>
                  </button>
                  <div style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', transition: 'grid-template-rows .3s ease' }}>
                    <div style={{ overflow: 'hidden' }}>
                      <p className="muted pretty" style={{ margin: 0, paddingBottom: 24, fontSize: 15.5, maxWidth: 680 }}>{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
